import { z } from 'zod'
import { isValidIpAddress } from '@/utils/ipAddress'

export const AbuseCategory = z.enum([
  'spam',
  'phishing',
  'malware',
  'fake_data',
  'trademark',
  'other'
])
export type AbuseCategory = z.infer<typeof AbuseCategory>

/**
 * Notice under Art. 16 DSA.
 *
 * The captcha field is named `token` to match what the backend binds. It
 * previously had no representation here at all while the component sent
 * `captcha_token`, so every notice failed binding with a 400 — the statutory
 * reporting channel had never accepted a single report.
 */
export const AbuseReportRequest = z.object({
  reporter_email: z.string().email(),
  /**
   * Art. 16(2)(c) DSA requires name and e-mail, except for notices concerning
   * offences under Art. 3-7 of Directive 2011/93/EU, which must remain
   * submittable anonymously. Optional here; the backend requires it for every
   * category except `other`, which is where such a report would land.
   */
  reporter_name: z.string().optional(),
  /** Exactly one of domain_name and reported_ip is sent. */
  domain_name: z.string().min(1).optional(),
  /** An IPv4 or IPv6 address of one of our servers, e.g. the source of spam. */
  reported_ip: z.string().refine((v) => isValidIpAddress(v)).optional(),
  /**
   * Art. 16(2)(b) DSA: precise electronic location, not just the domain.
   *
   * Restricted to http(s): a bare `.url()` accepts `javascript:` and `data:`,
   * and this field exists precisely so an admin can click through to the
   * reported content — which is how a permissive scheme becomes stored XSS.
   * Required for a domain report; an IP report may have no page to name.
   */
  url: z
    .string()
    .url()
    .refine((v) => /^https?:\/\//i.test(v), { message: 'URL must start with http:// or https://' })
    .optional(),
  category: AbuseCategory,
  /** Art. 16(2)(a) DSA: substantiated explanation of the alleged illegality. */
  description: z.string().min(10).max(2000),
  /** Art. 16(2)(d) DSA: good-faith declaration. Must be true. */
  good_faith: z.literal(true),
  token: z.string().min(1)
})
  .refine((r) => (r.domain_name === undefined) !== (r.reported_ip === undefined), {
    message: 'Exactly one of domain_name and reported_ip is required'
  })
  .refine((r) => r.domain_name === undefined || r.url !== undefined, {
    message: 'url is required for a domain report'
  })
export type AbuseReportRequest = z.infer<typeof AbuseReportRequest>

export const AbuseReportResponse = z.object({
  message: z.string().optional(),
  /** Case reference — the Art. 16(4) DSA confirmation-of-receipt artefact. */
  case_id: z.number().int().optional(),
  /**
   * True when the reported domain is not one we operate. The notice is still
   * recorded, but we are not the right addressee.
   */
  out_of_scope: z.boolean().optional()
})
export type AbuseReportResponse = z.infer<typeof AbuseReportResponse>
